import { Menu } from "lucide-react"

import { useEffect, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { toast } from "sonner"

import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Toaster } from "@/components/ui/sonner"
import { cn } from "@/lib/utils"
import { CUSTOMER_BEACON, POSTING_BEACON, loadHelpBeacon, openHelp } from "@/lib/helpBeacon"
import SidebarContent from "./SidebarContent"
import TopbarSearch from "./TopbarSearch"
import { BellMenu, MessagesMenu } from "./NotificationsMenu"
import { ShellActivityProvider, useShellActivity } from "./ShellActivity"

// Responsive shell (Drawbridge, P8-H2). Breakpoints are the design bundle's
// 1200/860/560 (design/spec/shared/pm.css), written as Tailwind arbitrary
// max-width variants — v4 compiles `max-[860px]` to `@media (width < 860px)`,
// so "≤860" in the design reads as "below 860" here; the one-pixel boundary
// nibble is invisible on real devices. Desktop (≥1200) is untouched: every new
// rule is gated behind a max-[...] variant.
//
// ≤1200: nothing to do — the React shell never ported the design prototype's
// fixed 1440px canvas (`.app{width:1440px}`); the root is `w-full` flex and
// goes fluid on its own. ≤860: the static rail hides and the SAME
// SidebarContent renders inside an off-canvas left Sheet (Radix dialog gives
// the scrim, Escape, and focus containment) behind a topbar hamburger.
// ≤560: the topbar tightens and the breadcrumb collapses to the current crumb.

function Sidebar({ active, navCounts, auth, onHelp }) {
  return (
    <aside className="flex h-screen w-[224px] shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar px-[14px] py-[22px] max-[860px]:hidden">
      <SidebarContent active={active} navCounts={navCounts} auth={auth} onHelp={onHelp} />
    </aside>
  )
}

function Topbar({ crumb, unreadNotifications }) {
  return (
    <header className="flex shrink-0 items-center gap-[18px] border-b border-border bg-background px-[26px] py-[18px] max-[860px]:gap-3 max-[860px]:px-4 max-[860px]:py-[14px] max-[560px]:gap-[9px] max-[560px]:px-[13px] max-[560px]:py-[11px]">
      {/* Drawer hamburger — leftmost, only below 860. Radix's Trigger supplies
          aria-expanded/aria-haspopup and returns focus on close. */}
      <SheetTrigger
        aria-label="Open navigation"
        className="hidden size-10 shrink-0 items-center justify-center rounded-[10px] border border-border bg-card text-foreground transition-colors hover:bg-secondary max-[860px]:flex"
      >
        <Menu className="size-[19px]" strokeWidth={2} />
      </SheetTrigger>

      <nav className="flex items-center gap-[7px] text-[12.5px] font-semibold text-ink-3 max-[860px]:min-w-0 max-[860px]:overflow-hidden max-[860px]:text-[12px]">
        {crumb.map((c, i) => {
          const isLast = i === crumb.length - 1
          return (
            <span
              key={`${c}-${i}`}
              className={cn(
                "flex items-center gap-[7px]",
                // ≤560 the breadcrumb collapses to the current page: ancestor
                // crumbs and every separator hide; the current crumb bolds up
                // and truncates with an ellipsis.
                isLast ? "max-[860px]:min-w-0" : "max-[560px]:hidden"
              )}
            >
              {i > 0 && <span className="text-line-2 max-[560px]:hidden">/</span>}
              <span
                className={cn(
                  isLast &&
                    "text-foreground max-[560px]:truncate max-[560px]:text-[13px] max-[560px]:font-bold"
                )}
              >
                {c}
              </span>
            </span>
          )
        })}
      </nav>

      <TopbarSearch className="ml-[6px] max-w-[300px] flex-1 max-[860px]:hidden" />

      {/* Messages + Notifications dropdowns replace the old static icon buttons
          and the topbar avatar (the account block now lives in the sidebar foot). */}
      <div className="ml-auto flex items-center gap-[9px]">
        <MessagesMenu />
        <BellMenu unreadCount={unreadNotifications ?? 0} />
      </div>
    </header>
  )
}

function showFlash(flash) {
  if (flash?.alert) toast.error(flash.alert)
  else if (flash?.notice) toast.success(flash.notice)
}

// Surfaces the shared server `flash` prop as a Sonner toast — the DS end state
// for the old inline flash banner (Hallmark §5d). The trigger is the *visit*,
// not a change in the message string: AppShell persists across Inertia visits,
// so keying an effect on the flash strings drops a repeat action that returns
// the identical message. Instead we fire once on the initial (server-rendered)
// page load, then off every subsequent Inertia response via router's `success`
// event — which carries that visit's own flash and re-fires even when the
// message is identical to the last one. Mounted once in the shell, so every
// screen's flash routes here; client-only messages (e.g. OrderDetail's
// Stripe-return notice) still toast locally from their own page.
//
// Guard against partial reloads: Inertia merges omitted props from the current
// page before firing `success`, so a partial reload that excludes `flash`
// (FindFreelancers search `only: ["freelancers"]`, Team polling `only: ["team"]`)
// carries the PREVIOUS, stale flash and would re-toast it on every poll/search.
// We watch `start` for the in-flight visit's `.only` (@inertiajs/core 3.4.0:
// `start` detail is `{ visit }`, and `Visit.only` is a `string[]`), flag it as a
// flash-excluding partial reload, and skip the toast in the matching `success`
// (detail `{ page }` — visits are processed sequentially, so `success` fires
// right after its own `start`). Full navigations and actions (`only` empty, or
// `only` includes "flash") still toast every visit, so identical repeats fire.
function FlashBridge() {
  const initialFlash = usePage().props.flash
  useEffect(() => {
    showFlash(initialFlash)

    let skipNextFlash = false
    const offStart = router.on("start", (event) => {
      const only = event.detail.visit.only
      skipNextFlash = only.length > 0 && !only.includes("flash")
    })
    const offSuccess = router.on("success", (event) => {
      if (!skipNextFlash) showFlash(event.detail.page.props.flash)
    })

    return () => {
      offStart()
      offSuccess()
    }
    // Intentionally mount-only: initialFlash is captured for the first load;
    // all later visits are handled by the router listeners.
  }, [])
  return null
}

// The chrome every customer screen sits inside: fixed rail + topbar. The
// signed-in customer (auth) is a shared Inertia prop, so the account block reads
// the real person on every page; navCounts is per-page and optional.
function Shell({ active, crumb, children }) {
  const { auth, navCounts, unreadNotifications, liveChat, helpIdentity } = usePage().props
  const [navOpen, setNavOpen] = useState(false)
  const { unread: liveUnread } = useShellActivity()

  // The rail's Messages badge reads the SAME live tally as the topbar dot, so it
  // is correct on every page — not only /messages, the one page whose presenter
  // supplies navCounts.messages. Same unit (unread conversations), and before the
  // first fetch the live value IS the shared prop, so the two cannot disagree.
  const counts = { ...navCounts, messages: liveUnread }

  // Post a Project keeps its own support Beacon, as the legacy posting pages did.
  useEffect(() => {
    if (liveChat) loadHelpBeacon(active === "post" ? POSTING_BEACON : CUSTOMER_BEACON, helpIdentity)
  }, [active, liveChat, helpIdentity])

  // Side nav Support › Get help. Closes the drawer first so the help panel is
  // not left under the drawer's modal layer on small screens.
  const openHelpPanel = () => {
    setNavOpen(false)
    openHelp(() =>
      toast.error("Help couldn't load. An ad blocker or browser extension may be blocking it; you can also email support@dibbly.com.")
    )
  }
  const onHelp = liveChat ? openHelpPanel : undefined

  // Close the open drawer when the customer actually goes somewhere: AppShell
  // persists across Inertia visits, so drawer links never unmount the shell.
  // Trigger on the visit's `start` (immediate feedback) but ONLY for full
  // visits — partial reloads (`only: [...]`, e.g. Team's polling or the
  // FindFreelancers search) fire router events without navigation intent and
  // must not yank the drawer shut. Also close if the viewport grows back to
  // static-rail territory (≥860 — tablet rotation, window resize) so an open
  // dialog can't linger invisibly behind the desktop layout.
  useEffect(() => {
    if (!navOpen) return undefined

    const offStart = router.on("start", (event) => {
      if (event.detail.visit.only.length === 0) setNavOpen(false)
    })

    const mq = window.matchMedia("(min-width: 860px)")
    const onChange = () => {
      if (mq.matches) setNavOpen(false)
    }
    mq.addEventListener("change", onChange)

    return () => {
      offStart()
      mq.removeEventListener("change", onChange)
    }
  }, [navOpen])

  return (
    <Sheet open={navOpen} onOpenChange={setNavOpen}>
      {/* h-dvh only below 860: dvh tracks dynamic browser chrome, so on ≥860
          viewports that still show collapsing UI (landscape tablets) it would
          diverge from today's h-screen — the desktop-unchanged contract keeps
          vh there. The rail stays h-screen outright (hidden below 860). */}
      <div className="flex h-screen w-full overflow-hidden bg-background text-foreground max-[860px]:h-dvh">
        {/* Toaster before FlashBridge: React runs sibling mount effects in document
            order, so sonner's <Toaster/> subscribes before FlashBridge's mount
            effect fires showFlash(initialFlash). Sonner (2.0.7) does not replay to
            a late subscriber, so a Toaster mounted after FlashBridge drops the
            initial server-rendered flash on a full-page load. */}
        <Toaster position="top-center" />
        <FlashBridge />
        <Sidebar active={active} navCounts={counts} auth={auth} onHelp={onHelp} />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Topbar crumb={crumb} unreadNotifications={unreadNotifications} />
          {/* scroll-region: the shell is overflow-hidden, so Inertia's post-visit
              `window.scrollTo(0,0)` reaches nothing and this <main> — a
              persistent layout node — carried the previous page's scrollTop
              into every visit. Inertia resets/restores ONLY elements carrying
              this attribute, BY INDEX into that NodeList, so it must stay the
              one marked region in the app: nested scrollers (chat threads, the
              rail) are deliberately unmarked and own their own scrolling.
              Proved in e2e/tests/84-inertia-scroll.spec.ts. */}
          <main scroll-region="" className="min-h-0 flex-1 overflow-y-auto">
            {children}
          </main>
        </div>

        {/* Off-canvas nav drawer (≤860 — the trigger above is its only opener).
            Same SidebarContent as the static rail; design width 272px. Portal-
            rendered, so its position here doesn't affect shell layout. */}
        <SheetContent
          side="left"
          aria-describedby={undefined}
          className="w-[272px] gap-0 border-sidebar-border bg-sidebar"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-[14px] py-[22px]">
            <SidebarContent active={active} navCounts={counts} auth={auth} onHelp={onHelp} />
          </div>
        </SheetContent>
      </div>
    </Sheet>
  )
}

// The shell's one activity refresher wraps the whole chrome. AppShell persists
// across Inertia visits, so it is mounted exactly once for the session: every
// subscriber (topbar dropdown, rail badge, Messages inbox, order Chat tab) shares
// that single poll cycle rather than arming its own. `unreadMessages` seeds it so
// the dot and the badge are right on first paint, before any fetch lands.
export default function AppShell({
  active = "Dashboard",
  crumb = ["Dashboard"],
  children,
}) {
  const { unreadMessages } = usePage().props

  return (
    <ShellActivityProvider unreadCount={unreadMessages ?? 0}>
      <Shell active={active} crumb={crumb}>
        {children}
      </Shell>
    </ShellActivityProvider>
  )
}
